@file:Suppress("TooGenericExceptionCaught")

package com.nonopn.skydome.common.app.pages.estimates

import com.nonopn.dispatchers.DefaultDispatcher
import com.nonopn.skydome.common.app.api.Mode
import com.nonopn.skydome.common.app.api.impl.AdminEstimatePatch
import com.nonopn.skydome.common.app.api.impl.ApiAdmin
import com.nonopn.skydome.common.app.api.impl.ApiInterface
import com.nonopn.skydome.common.app.api.impl.EstimateRequest
import com.nonopn.skydome.common.app.api.impl.ThunderDetectionEstimate
import com.nonopn.skydome.common.app.api.impl.canToggleActive
import com.nonopn.skydome.common.app.api.impl.toEstimate
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.pages.main.model.LoadStrikeRadarData
import com.nonopn.skywatcher.common.api.ApiSession
import com.nonopn.skywatcher.maps.LatLng
import com.nonopn.skywatcher.design.widgets.abslists.AbstractListModel
import com.nonopn.skywatcher.design.widgets.abslists.AbstractListModelState
import com.nonopn.skywatcher.design.widgets.abslists.ListModelState
import com.nonopn.skywatcher.design.widgets.abslists.ListModelStateHolder
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import korlibs.time.DateTime
import ovh.plrapps.mapcompose.api.ExperimentalClusteringApi

data class EstimatesModelState(
    val loading: Boolean = false,
    val initialized: Boolean = false,
    val apiSession: ApiSession? = null,
    // True when the last activate / deactivate request failed
    val toggleError: Boolean = false,
    // Search text and status chip of the list (admin and user lists)
    val searchQuery: String = "",
    val statusFilter: EstimateStatusFilter = EstimateStatusFilter.ALL,
    override val modelState: ListModelStateHolder<ThunderDetectionEstimate> = ListModelStateHolder()
) : AbstractListModelState<ThunderDetectionEstimate>

@OptIn(FlowPreview::class)
class EstimatesModel(
    private val appModel: AppModel,
    private val mode: Mode
) : AbstractListModel<EstimatesModelState, ThunderDetectionEstimate>(EstimatesModelState()) {
    init {
        safeLaunch {
            updateState {
                copy(
                    initialized = true
                )
            }

            load()
        }
    }

    override fun onCleared() {
        println("CLEARED MainScreenModel")

        super.onCleared()
    }

    private val scope = CoroutineScope(DefaultDispatcher + SupervisorJob())

    private val currentApiInterfaces: ApiInterface
        get() = when (mode) {
            Mode.Normal -> appModel.states.value.apiProvider?.apiNormal!!
            Mode.Admin -> appModel.states.value.apiProvider?.apiAdmin!!
        }

    @OptIn(ExperimentalClusteringApi::class)
    private fun startLoadingEstimates() = safeLaunch {
        scope.async {
            updateState {
                copy(
                    loading = true,
                    modelState = modelState.copy(state = ListModelState.LOADING)
                )
            }

            val result = try {
                loadEstimates()
            } catch (_: LoadStrikeRadarData.LoadStrikeRadarCanceledException) {
                // nothing
                return@async
            } catch (other: Throwable) {
                other.printStackTrace()

                updateState {
                    copy(
                        loading = false,
                        modelState = modelState.copy(state = ListModelState.ERROR)
                    )
                }
                return@async
            }

            println("having result ${result.size} size")

            result.forEach { appModel.cacheEstimate(it) }

            updateState {
                copy(
                    loading = false,
                    modelState = modelState.copy(
                        state = ListModelState.LOADED,
                        objects = result,
                        filtered = filterEstimates(result, searchQuery, statusFilter, nowSeconds())
                    )
                )
            }
        }
    }

    /**
     * Admin: the estimates of GET /api/admin/estimates only (state, anomalies, payment type). There is no
     * fallback on the former admin route: an error is shown as usual. User: unchanged.
     */
    private suspend fun loadEstimates(): List<ThunderDetectionEstimate> {
        val admin = currentApiInterfaces as? ApiAdmin ?: return currentApiInterfaces.estimates()
        return admin.adminEstimates().map { it.toEstimate() }
    }

    override fun load() {
        startLoadingEstimates()
    }

    /**
     * Activates / deactivates a whole estimate (all its probes). Estimates read from the admin route use
     * PATCH /api/admin/estimates/{uuid} (External only: Stripe and demo estimates are read-only). Other
     * estimates use the same request as the estimate edit screen, keeping every other field as it is.
     */
    fun setEnabled(estimate: ThunderDetectionEstimate, enabled: Boolean) = safeLaunch {
        try {
            val api = currentApiInterfaces
            if (estimate.admin != null && api is ApiAdmin) {
                // The UI only offers the switch for External estimates; refuse anything else here too
                check(estimate.canToggleActive()) { "Only an External estimate can be activated" }
                api.patchAdminEstimate(estimate.uuid, AdminEstimatePatch(active = enabled))
            } else {
                api.createEstimate(
                    EstimateRequest(
                        uuid = estimate.uuid,
                        email = estimate.email,
                        isSkySentinel = estimate.isSkySentinel,
                        positions = estimate.probes.map {
                            LatLng(latitude = it.latitude, longitude = it.longitude)
                        },
                        validUntilSecondsUtc = estimate.validUntilSecondsUtc,
                        areEnabled = enabled
                    )
                )
            }
            appModel.invalidateDashboardCache()
            updateState { copy(toggleError = false) }
            load()
        } catch (other: Throwable) {
            other.printStackTrace()
            updateState { copy(toggleError = true) }
        }
    }

    fun dismissToggleError() {
        updateState { copy(toggleError = false) }
    }

    /** Applies the search text and the status chip on the loaded estimates. */
    fun applyFilter(query: String, status: EstimateStatusFilter) {
        updateState {
            copy(
                searchQuery = query,
                statusFilter = status,
                modelState = modelState.copy(
                    filtered = filterEstimates(modelState.objects, query, status, nowSeconds())
                )
            )
        }
    }

    private fun nowSeconds() = DateTime.now().unixMillisLong / 1000L

    override fun isMatch(
        entity: ThunderDetectionEstimate,
        filter: String
    ) = filterEstimates(listOf(entity), filter, states.value.statusFilter, nowSeconds()).isNotEmpty()

    override fun onNewStateHolder(newHolder: ListModelStateHolder<ThunderDetectionEstimate>) {
        updateState {
            copy(modelState = newHolder)
        }
    }
}
